import { Component, OnInit, computed, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faBolt, faClock, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { PackageSpeed, ShippingConfig } from '../../../../_models/shipping-config.interface';
import { offsetWordingFor } from '../../../../_models/shipping-config-rules';
import {
  ShippingConfigService,
  ShippingSchedulePayload,
} from '../../../../_services/shipping-config.service';
import { addWorkingDays, subtractWorkingDays } from '../../../../_helpers/working-days';
import {
  DEFAULT_OFFSET_WORKING_DAYS,
  STAGING_DELAY_WORKING_DAYS,
  formatLocalDate,
} from '../../../../_models/shipping-schedule-defaults';

export type ScheduleChannel = 'STANDARD' | 'STAGING';

/**
 * Laufzeit-Annahmen für die Ankunfts-Vorschau (Werktage, Samstag zählt mit,
 * keine Feiertage). Nur für Paketversand — bei Palette, Kurier und Abholung
 * macht der Dialog bewusst keine Angabe.
 */
const TRANSIT_WORKING_DAYS = { express: 2, standard: 3 } as const;

/**
 * Grenze für die Vorlauf-Eingabe. Ein Jahr Vorlauf ist schon jenseits jeder
 * Praxis; die Grenze schützt vor Vertippern, die die tageweise rechnende
 * Werktags-Vorschau ausbremsen würden.
 */
const MAX_OFFSET_DAYS = 365;

/** Eingabe auf eine ganze Zahl zwischen 0 und MAX_OFFSET_DAYS bringen. */
function clampOffsetDays(value: unknown): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.min(Math.max(Math.floor(parsed), 0), MAX_OFFSET_DAYS);
}

/**
 * Dialog für den Versandzeitpunkt EINES Kanals — erreichbar über die Datumszelle
 * der Versand-Matrix. Eine Komponente für beide Kanäle: Modi, Vorschau und
 * Validierung sind identisch, der Staging-Kanal hat zusätzlich die
 * KW-Zuordnung und speichert auf 09:00 Uhr.
 */
@Component({
  standalone: true,
  selector: 'hawk-shipping-schedule-dialog',
  imports: [FormsModule, FontAwesomeModule],
  templateUrl: './shipping-schedule-dialog.component.html',
})
export class ShippingScheduleDialogComponent implements OnInit {
  private configService = inject(ShippingConfigService);

  readonly config = input.required<ShippingConfig>();
  readonly channel = input.required<ScheduleChannel>();
  readonly operationId = input.required<number>();
  /** Auftragsdatum als Bezugspunkt der Regel. */
  readonly referenceDate = input<string | undefined>(undefined);
  /** Geplante Staging-Kalenderwoche — nur zur Anzeige im KW-Modus. */
  readonly stagingKw = input<number | null>(null);
  /**
   * Reiner Versandauftrag (Ticket-Priorität mit Versand-Flag). Dort IST das
   * Auftragsdatum der Versandtag — Material muss an diesem Termin nicht vor Ort
   * sein, die Ankunft danach ist also der Normalfall und keine Warnung wert.
   */
  readonly shippingOrder = input<boolean>(false);

  readonly saved = output<ShippingConfig>();
  readonly closed = output<void>();

  protected iconInfo = faInfoCircle;
  protected speeds = [
    { value: 'standard' as PackageSpeed, label: 'Standard', icon: faClock },
    { value: 'express' as PackageSpeed, label: 'Express', icon: faBolt },
  ];

  protected mode = signal<'offset' | 'date' | 'kw'>('offset');
  protected latestOffsetDays = signal<number>(0);
  protected allowEarlier = signal<boolean>(false);
  protected earliestOffsetDays = signal<number>(0);
  protected fixedDate = signal<string>('');
  protected packageSpeed = signal<PackageSpeed | null>(null);
  protected saving = signal<boolean>(false);
  protected errorMessage = signal<string | null>(null);

  protected isStaging = computed(() => this.channel() === 'STAGING');

  /** PickPoint rechnet gegen den Abholtermin, alle anderen Typen gegen das Auftragsdatum. */
  protected wording = computed(() => offsetWordingFor(this.config().shipping_type));

  /** Versandfenster ist bei PickPoint nicht wählbar — dort taktet die Abholung. */
  protected windowAllowed = computed(() => !this.isStaging() && this.config().shipping_type !== 'pickpoint');

  ngOnInit(): void {
    const c = this.config();
    this.packageSpeed.set(c.package_speed ?? null);
    if (this.isStaging()) {
      this.initStaging(c);
    } else {
      this.initStandard(c);
    }
  }

  private initStandard(c: ShippingConfig): void {
    if (c.earliest_shipping_offset_days != null) {
      this.mode.set('offset');
      this.allowEarlier.set(true);
      this.latestOffsetDays.set(c.shipping_date_offset_days ?? 0);
      this.earliestOffsetDays.set(c.earliest_shipping_offset_days);
      return;
    }
    if (c.shipping_date_offset_days != null) {
      this.mode.set('offset');
      this.latestOffsetDays.set(c.shipping_date_offset_days);
      this.earliestOffsetDays.set(c.shipping_date_offset_days);
      return;
    }
    if (c.shipping_date) {
      this.mode.set('date');
      this.fixedDate.set(c.shipping_date.slice(0, 10));
      return;
    }
    this.mode.set('offset');
    this.latestOffsetDays.set(this.fallbackOffsetDays());
    this.earliestOffsetDays.set(this.fallbackOffsetDays());
  }

  private initStaging(c: ShippingConfig): void {
    if (c.staging_ship_offset_days != null) {
      this.mode.set('offset');
      this.latestOffsetDays.set(c.staging_ship_offset_days);
      return;
    }
    if (c.staging_ship_ready_at) {
      this.mode.set('date');
      this.fixedDate.set(c.staging_ship_ready_at.slice(0, 10));
      return;
    }
    this.mode.set('kw');
    this.latestOffsetDays.set(STAGING_DELAY_WORKING_DAYS);
  }

  /** Vorschlag, wenn die Config noch gar keinen Termin trägt. */
  private fallbackOffsetDays(): number {
    return this.config().shipping_type === 'pickpoint'
      ? STAGING_DELAY_WORKING_DAYS
      : DEFAULT_OFFSET_WORKING_DAYS;
  }

  // ===== Vorschau =====

  /** Bezugspunkt der Regel: PickPoint rechnet gegen den Abholtermin. */
  private referenceForRule = computed<Date | null>(() => {
    const c = this.config();
    const basis = c.shipping_type === 'pickpoint'
      ? (c.pickup_date ?? this.referenceDate())
      : this.referenceDate();
    if (!basis) return null;
    const date = new Date(basis);
    return Number.isNaN(date.getTime()) ? null : date;
  });

  /** Spätester Versandtag (im Datums-Modus der gewählte Tag). */
  protected shipDay = computed<Date | null>(() => {
    if (this.mode() === 'kw') return null;
    if (this.mode() === 'date') {
      if (!this.fixedDate()) return null;
      const date = new Date(`${this.fixedDate()}T00:00`);
      return Number.isNaN(date.getTime()) ? null : date;
    }
    const reference = this.referenceForRule();
    return reference ? subtractWorkingDays(reference, Number(this.latestOffsetDays())) : null;
  });

  /** Frühester Versandtag des Fensters. */
  protected earliestDay = computed<Date | null>(() => {
    if (this.mode() !== 'offset' || !this.allowEarlier() || !this.windowAllowed()) return null;
    const reference = this.referenceForRule();
    return reference ? subtractWorkingDays(reference, Number(this.earliestOffsetDays())) : null;
  });

  /** Laufzeit-Annahme — nur bei Paketversand. */
  protected transitDays = computed<number | null>(() => {
    if (this.config().shipment_method !== 'parcel') return null;
    return this.packageSpeed() === 'express'
      ? TRANSIT_WORKING_DAYS.express
      : TRANSIT_WORKING_DAYS.standard;
  });

  protected arrivalDay = computed<Date | null>(() => {
    const ship = this.shipDay();
    const transit = this.transitDays();
    return ship && transit ? addWorkingDays(ship, transit) : null;
  });

  /** Ankunft nach dem Auftragstermin → das Material ist zu spät vor Ort. */
  protected arrivalTooLate = computed<boolean>(() => {
    // Beim reinen Versandauftrag gibt es keinen Einbau, vor dem das Material da
    // sein müsste — das Auftragsdatum ist dort der Versandtag.
    if (this.shippingOrder()) return false;

    const arrival = this.arrivalDay();
    const reference = this.referenceDate();
    if (!arrival || !reference) return false;
    const operationDay = new Date(reference);
    if (Number.isNaN(operationDay.getTime())) return false;
    operationDay.setHours(23, 59, 59, 999);
    return arrival.getTime() > operationDay.getTime();
  });

  protected formatDay(date: Date | null): string {
    if (!date) return '—';
    return date.toLocaleDateString('de-DE', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  // ===== Eingaben =====

  protected setLatestOffsetDays(value: unknown): void {
    this.latestOffsetDays.set(clampOffsetDays(value));
  }

  protected setEarliestOffsetDays(value: unknown): void {
    this.earliestOffsetDays.set(clampOffsetDays(value));
  }

  protected setToday(): void {
    this.fixedDate.set(formatLocalDate(new Date()));
  }

  protected setTomorrow(): void {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    this.fixedDate.set(formatLocalDate(date));
  }

  /**
   * Schnellweg für den Eilfall: fester Termin heute plus Express. Setzt nur die
   * Felder — gespeichert wird wie sonst auch über „Speichern", damit der Klick
   * nicht versehentlich einen Termin festschreibt.
   */
  protected setExpressToday(): void {
    this.mode.set('date');
    this.setToday();
    this.packageSpeed.set('express');
  }


  /**
   * Die Paket-Geschwindigkeit betrifft immer nur die noch nicht rausgegangene
   * Sendung — deshalb zählt der Kanal dieses Dialogs, nicht die ganze Config.
   * Ist der Standardversand raus, bleibt sie für den Staging-Versand änderbar.
   */
  protected speedEditable = computed(() => {
    const config = this.config();
    if (config.shipment_method !== 'parcel') return false;
    return this.isStaging() ? !config.staging_shipped : !config.versand_shipped;
  });

  /** „Frühestens" muss MEHR Werktage vor dem Auftrag liegen als „spätestens". */
  protected windowInvalid = computed(() =>
    this.mode() === 'offset'
      && this.allowEarlier()
      && this.windowAllowed()
      && Number(this.earliestOffsetDays()) < Number(this.latestOffsetDays()),
  );

  protected canSave = computed(() => {
    if (this.saving() || this.windowInvalid()) return false;
    if (this.mode() === 'date') return this.fixedDate().length > 0;
    if (this.mode() === 'offset') return Number(this.latestOffsetDays()) >= 0;
    return true;
  });

  // ===== Speichern =====

  private buildPayload(): ShippingSchedulePayload {
    const payload: ShippingSchedulePayload = {};
    const offset = this.mode() === 'offset' ? Number(this.latestOffsetDays()) : null;

    if (this.isStaging()) {
      payload.staging_ship_offset_days = offset;
      // Fester Termin: das Lager darf ab 09:00 losschicken — gleiche Uhrzeit,
      // die auch die Offset-Regel im Backend materialisiert.
      payload.staging_ship_ready_at = this.mode() === 'date' ? `${this.fixedDate()}T09:00` : null;
    } else {
      payload.shipping_date_offset_days = offset;
      payload.shipping_date = this.mode() === 'date' ? this.fixedDate() : null;
      payload.earliest_shipping_offset_days =
        this.mode() === 'offset' && this.allowEarlier() && this.windowAllowed()
          ? Number(this.earliestOffsetDays())
          : null;
    }

    if (this.speedEditable()) payload.package_speed = this.packageSpeed();
    return payload;
  }

  protected async onSave(): Promise<void> {
    if (!this.canSave()) return;
    this.saving.set(true);
    this.errorMessage.set(null);
    try {
      const updated = await this.configService.updateConfigSchedule(
        this.operationId(),
        this.config().id,
        this.isStaging() ? 'staging' : 'standard',
        this.buildPayload(),
      );
      this.saved.emit(updated);
    } catch (err) {
      const body = (err as { error?: { error?: string } } | undefined)?.error;
      this.errorMessage.set(body?.error || 'Speichern fehlgeschlagen — bitte erneut versuchen.');
    } finally {
      this.saving.set(false);
    }
  }
}
